import { useRef, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import MarkdownEditor from '../../components/markdown/MarkdownEditor';
import Spinner from '../../components/Spinner';
import { errorMessage, triageAttachmentUrl } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { VulnerabilityEventRow } from '../../lib/types';
import {
  buildAdvisory,
  buildCustomerNotice,
  DEFAULT_ADVISORY_TEMPLATE,
  importAdvisoryTemplate,
  MAX_ADVISORY_LENGTH,
} from './advisoryTemplate';
import type { TriageDocumentState } from './useTriageDocuments';

interface PanelProps {
  event: VulnerabilityEventRow;
  state: TriageDocumentState;
  disabled: boolean;
  canWrite: boolean;
}

export function DocumentIcon({
  kind = 'file',
}: {
  kind?: 'file' | 'upload' | 'download';
}): ReactNode {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'file' ? (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6M8 13h8M8 17h5" />
        </>
      ) : (
        <>
          <path d="M4 16v4h16v-4M12 3v12" />
          <path d={kind === 'upload' ? 'm7 8 5-5 5 5' : 'm7 10 5 5 5-5'} />
        </>
      )}
    </svg>
  );
}

function downloadMarkdown(content: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function AdvisoryPanel({
  event,
  state,
  disabled,
  canWrite,
  onSave,
}: PanelProps & { onSave?: () => Promise<void> }): ReactNode {
  const importRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<{ content: string; name: string } | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const locked = disabled || reading;
  const placeholders = new Set(
    Array.from(state.content.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g), (match) => match[1]),
  ).size;
  // The formatted view cannot enforce maxLength while typing; block saving instead.
  const tooLong = state.content.length > MAX_ADVISORY_LENGTH;

  function apply(content: string, name: string): void {
    state.setContent(content);
    state.setTemplateName(name);
    setCandidate(null);
  }

  function propose(content: string, name: string): void {
    setLocalError(null);
    if (state.content.trim()) setCandidate({ content, name });
    else apply(content, name);
  }

  async function importFile(file: File): Promise<void> {
    setReading(true);
    state.setPreparing(true);
    setLocalError(null);
    try {
      if (!/\.(md|txt)$/i.test(file.name))
        throw new Error('Bitte eine Markdown- oder Textdatei (.md, .txt) auswählen.');
      if (file.size > MAX_ADVISORY_LENGTH * 4) throw new Error('Die Vorlagendatei ist zu groß.');
      const bytes = await file.arrayBuffer();
      let text: string;
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      } catch {
        throw new Error('Die Vorlage muss eine UTF-8-Textdatei sein.');
      }
      if (text.includes('\0')) throw new Error('Die Vorlage muss eine UTF-8-Textdatei sein.');
      propose(importAdvisoryTemplate(text, event), file.name);
    } catch (err) {
      setLocalError(errorMessage(err));
    } finally {
      setReading(false);
      state.setPreparing(false);
    }
  }

  return (
    <section className="triage-doc-panel" aria-label="Security Advisory (Kunden)">
      <div className="triage-panel-heading">
        <h3>Security Advisory (Kunden)</h3>
        <span className="triage-panel-status" role="status">
          <Chip tone={canWrite && state.dirty ? 'warn' : 'neutral'}>
            {canWrite && state.dirty
              ? 'Ungespeicherte Änderungen'
              : state.documents?.advisory.updated_at
                ? 'Gespeichert'
                : 'Noch nicht gespeichert'}
          </Chip>
        </span>
      </div>

      {canWrite && (
        <div className="triage-template-card">
          <div className="triage-template-icon">
            <DocumentIcon />
          </div>
          <div className="triage-template-copy">
            <strong>Vorlage wählen</strong>
            <span className="hint">Kurz-Vorlage: Kundenanschreiben · CRA-Vorlage: vollständiges Advisory</span>
          </div>
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={() => propose(buildCustomerNotice(event), 'Kurz-Vorlage · Kundenanschreiben')}
          >
            Kurz-Vorlage verwenden
          </button>
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={() => propose(buildAdvisory(event), 'CRA Vulnerability Advisory · Orbiq')}
          >
            CRA-Vorlage verwenden
          </button>
        </div>
      )}

      <div className="triage-document-toolbar">
        {canWrite && (
          <>
            <button
              type="button"
              className="ghost"
              disabled={locked}
              onClick={() => importRef.current?.click()}
            >
              <DocumentIcon kind="upload" />
              {reading ? 'Vorlage wird gelesen…' : 'Vorlage importieren'}
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".md,.txt,text/markdown,text/plain"
              hidden
              aria-label="Vorlagendatei importieren"
              disabled={locked}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void importFile(file);
              }}
            />
            <span className="hint">Markdown oder Text · bekannte Felder werden vorausgefüllt</span>
          </>
        )}
        <button
          type="button"
          className="link"
          onClick={() =>
            downloadMarkdown(DEFAULT_ADVISORY_TEMPLATE, 'cra-vulnerability-advisory-template.md')
          }
        >
          Vorlage herunterladen
        </button>
      </div>

      {localError && (
        <Banner kind="error" onDismiss={() => setLocalError(null)}>
          {localError}
        </Banner>
      )}
      {state.conflict && (
        <Banner
          kind="warn"
          sub="Laden Sie Ihren Text bei Bedarf herunter, bevor Sie die gespeicherte Version übernehmen."
        >
          Ein anderer Benutzer hat diesen Entwurf geändert.{' '}
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={() => setConfirmReload(true)}
          >
            Aktuelle Version laden
          </button>
        </Banner>
      )}
      {confirmReload && (
        <div className="triage-inline-confirm" role="alert">
          <div>
            <strong>Editor durch gespeicherte Version ersetzen?</strong>
            <p>Ungespeicherte Änderungen am Advisory werden verworfen.</p>
          </div>
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={() => setConfirmReload(false)}
          >
            Zurück
          </button>
          <button
            type="button"
            disabled={locked}
            onClick={() => {
              void state.load();
            }}
          >
            Aktuelle Version übernehmen
          </button>
        </div>
      )}
      {candidate && (
        <div className="triage-inline-confirm" role="alert">
          <div>
            <strong>Aktuellen Entwurf ersetzen?</strong>
            <p>
              „{candidate.name}“ ersetzt den Text im Editor. Gespeichert wird erst mit „Entwurf
              speichern“ oder der Bewertung.
            </p>
          </div>
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={() => setCandidate(null)}
          >
            Beibehalten
          </button>
          <button
            type="button"
            disabled={locked}
            onClick={() => apply(candidate.content, candidate.name)}
          >
            Entwurf ersetzen
          </button>
        </div>
      )}

      <div className="field triage-editor-field">
        <div className="triage-editor-label">
          <label id={`advisory-label-${state.scope.versionId}`} htmlFor={`advisory-${state.scope.versionId}`}>
            Text des Advisorys
          </label>
          {state.templateName && (
            <span className="hint" title={state.templateName}>
              {state.templateName}
            </span>
          )}
        </div>
        <MarkdownEditor
          id={`advisory-${state.scope.versionId}`}
          labelledBy={`advisory-label-${state.scope.versionId}`}
          describedBy={`advisory-hint-${state.scope.versionId}`}
          value={state.content}
          onChange={state.setContent}
          maxLength={MAX_ADVISORY_LENGTH}
          readOnly={!canWrite}
          disabled={locked}
          placeholder={
            canWrite
              ? 'Kurz- oder CRA-Vorlage verwenden oder eine eigene Vorlage importieren und den Text ausarbeiten…'
              : 'Zu dieser Produktversion wurde noch kein Security Advisory gespeichert.'
          }
        />
        <div className="triage-editor-meta" id={`advisory-hint-${state.scope.versionId}`}>
          <span>
            {placeholders > 0
              ? `${placeholders} offene Platzhalter · vor Weitergabe ergänzen`
              : 'Wird nach dem Speichern als Text der Kundenbenachrichtigung verwendet · nicht automatisch versendet'}
          </span>
          <span className={tooLong ? 'triage-editor-over' : undefined}>
            {state.content.length.toLocaleString('de-DE')} /{' '}
            {MAX_ADVISORY_LENGTH.toLocaleString('de-DE')}
          </span>
        </div>
        <span className="hint">
          Für die Kundenentwürfe wird der Text ohne Anleitungskommentare übernommen; die erste Überschrift wird zum Betreff.
        </span>
      </div>

      <div className="triage-document-toolbar">
        {canWrite && (
          <button
            type="button"
            className="primary"
            disabled={locked || !state.needsSave || tooLong}
            onClick={() => {
              void (onSave ?? state.save)().catch(() => undefined);
            }}
          >
            {state.busy && <Spinner variant="on-accent" />}Advisory speichern
          </button>
        )}
        <button
          type="button"
          className="ghost"
          disabled={!state.content.trim()}
          onClick={() =>
            downloadMarkdown(state.content, `${state.scope.cve}-${state.scope.versionId}-advisory.md`)
          }
        >
          <DocumentIcon kind="download" />
          Als Markdown herunterladen
        </button>
        {state.documents?.advisory.updated_at && (
          <span className="hint">
            Gespeichert {fmtDate(state.documents.advisory.updated_at)} ·{' '}
            {state.documents.advisory.updated_by}
          </span>
        )}
      </div>
    </section>
  );
}

export function AttachmentsPanel({ state, disabled, canWrite }: PanelProps): ReactNode {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const documents = state.documents;
  if (!documents) return null;
  const atLimit = documents.attachments.length >= documents.limits.max_files;

  return (
    <section className="triage-doc-panel" aria-label="Anhänge und Nachweise">
      <div className="triage-panel-heading">
        <h3>Anhänge & Nachweise</h3>
        <Chip className="triage-panel-status">
          {documents.attachments.length} / {documents.limits.max_files} Dateien
        </Chip>
      </div>
      {canWrite && (
        <>
          <div
            className={`triage-dropzone${dragging ? ' dragging' : ''}${disabled || atLimit ? ' disabled' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!disabled && !atLimit) setDragging(true);
            }}
            onDragLeave={(e) => {
              if (!(e.relatedTarget instanceof Node) || !e.currentTarget.contains(e.relatedTarget))
                setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (!disabled && !atLimit) void state.upload(Array.from(e.dataTransfer.files));
            }}
          >
            <div className="triage-drop-icon">
              <DocumentIcon kind="upload" />
            </div>
            <strong>{atLimit ? 'Maximale Anzahl erreicht' : 'Dateien hier ablegen'}</strong>
            <span className="muted">oder vom Computer auswählen</span>
            <button
              type="button"
              className="ghost"
              disabled={disabled || atLimit}
              onClick={() => fileInput.current?.click()}
            >
              Dateien auswählen
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              aria-label="Anhänge hochladen"
              accept={documents.limits.allowed_extensions.join(',')}
              disabled={disabled || atLimit}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                void state.upload(files);
              }}
            />
            <span className="hint">
              Bis {formatSize(documents.limits.max_file_size)} pro Datei · maximal{' '}
              {documents.limits.max_files} Anhänge
            </span>
            <span className="hint triage-file-formats">
              {documents.limits.allowed_extensions
                .map((extension) => extension.slice(1).toUpperCase())
                .join(' · ')}
            </span>
          </div>
          <p className="triage-persistence-note">
            Anhänge werden beim Hochladen sofort zum Event gespeichert und bleiben beim Schließen
            erhalten.
          </p>
        </>
      )}

      <div className="triage-file-list" aria-live="polite" aria-relevant="additions text">
        {state.queue.map((item) => (
          <div
            className={`triage-file-row${item.status === 'failed' ? ' failed' : ''}`}
            key={item.id}
          >
            <div className="triage-file-icon">
              {item.status === 'failed' ? <DocumentIcon /> : <Spinner />}
            </div>
            <div className="triage-file-info">
              <strong>{item.file.name}</strong>
              <span>
                {formatSize(item.file.size)} ·{' '}
                {item.status === 'uploading'
                  ? 'Wird gespeichert…'
                  : item.status === 'queued'
                    ? 'Wartet auf Upload…'
                    : item.error}
              </span>
            </div>
            {item.status === 'failed' && (
              <div className="triage-file-actions">
                <button
                  type="button"
                  className="ghost"
                  disabled={disabled}
                  onClick={() => {
                    void state.upload([item.file], item.id);
                  }}
                >
                  Erneut versuchen
                </button>
                <button
                  type="button"
                  className="link"
                  disabled={disabled}
                  onClick={() => state.dismissQueued(item.id)}
                  aria-label={`${item.file.name} aus Upload-Liste entfernen`}
                >
                  Verwerfen
                </button>
              </div>
            )}
          </div>
        ))}
        {documents.attachments.map((file) => (
          <div className="triage-file-row" key={file.id}>
            <div className="triage-file-icon">
              <DocumentIcon />
            </div>
            <div className="triage-file-info">
              <strong>{file.filename}</strong>
              <span>
                {formatSize(file.size)} · {fmtDate(file.uploaded_at)} · {file.uploaded_by}
              </span>
              <span className="triage-file-saved">Gespeichert</span>
            </div>
            {deleteId === file.id ? (
              <div className="triage-file-actions" role="alert">
                <span>Anhang löschen?</span>
                <button
                  type="button"
                  className="ghost"
                  disabled={disabled}
                  onClick={() => setDeleteId(null)}
                >
                  Behalten
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={disabled}
                  onClick={() => {
                    void state.remove(file.id).then(() => setDeleteId(null));
                  }}
                >
                  Löschen
                </button>
              </div>
            ) : (
              <div className="triage-file-actions">
                <a
                  className="triage-download-link"
                  href={triageAttachmentUrl(state.scope, file.id)}
                  download={file.filename}
                  aria-label={`${file.filename} herunterladen`}
                >
                  <DocumentIcon kind="download" />
                  <span>Herunterladen</span>
                </a>
                {canWrite && (
                  <button
                    type="button"
                    className="link triage-remove-file"
                    disabled={disabled}
                    onClick={() => setDeleteId(file.id)}
                    aria-label={`${file.filename} löschen`}
                  >
                    Entfernen
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {documents.attachments.length === 0 && state.queue.length === 0 && (
          <EmptyState compact title="Noch keine Anhänge gespeichert" />
        )}
      </div>
    </section>
  );
}
